import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';
import { PublicSettingDto } from '../../helper/dto/public-settings.dto';
import { AuthApiService } from '../../services/auth-api.service';
import { TwoFactorDto } from '../../helper/dto/two-factor.dto';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { validateEmail } from '../../helper/auth.helper';
import { SelectModule } from 'primeng/select';
import { ToastService } from '../../services/toast.service';
import { OrgApiService } from '../../services/org-api.service';
import { OrgService } from '../../services/org.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ButtonModule, CheckboxModule, InputTextModule, PasswordModule, FormsModule, RouterModule, RippleModule, TranslatePipe, SelectModule],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    private readonly themeService = inject(ThemeService);

    /** Signal avoids NG0100 when BehaviorSubject already has theme data on subscribe. */
    readonly themeData = toSignal(this.themeService.themeData, { initialValue: null });

    oidcData: PublicSettingDto[] = [];

    oidcEnabled = false;
    oidcForceLogin = false;
    oidcButtonText = '';
    localEnabled = true;
    orgSlug: string | null = null;
    showSpinner = false;

    credentials = {
        email: '',
        password: ''
    };

    resetToken = '';
    newPassword = '';
    newPasswordRepeat = '';

    validationErrors: { [key: string]: string } = {};

    twofa = {
        code: '',
        selectedDevice: ''
    };
    stage = 'login';

    twoFactorDevices: TwoFactorDto[] = [];
    private loginRedirectInFlight: Promise<void> | null = null;

    constructor(
        private readonly router: Router,
        private authService: AuthService,
        private authApiService: AuthApiService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private toastService: ToastService,
        private orgApiService: OrgApiService,
        private orgService: OrgService
    ) { }

    ngOnInit(): void {
        void this.themeService.getData().then(() => {
            void this.themeService.updateTitle('Login');
        });

        this.route.queryParams.pipe(take(1)).subscribe((params) => {
            this.orgSlug = params['org'] ?? null;
            this.resetToken = typeof params['reset'] === 'string' ? params['reset'] : '';
            if (this.resetToken) {
                this.stage = 'reset';
            }
            this.loadAuthConfig();
        });

        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            if (params['confirmed']) {
                this.toastService.showSuccessToast('Anmeldung bestätigt', 'Sie können sich jetzt anmelden.');
            }
            if (params['denied']) {
                this.toastService.showSuccessToast('Anmeldung abgelehnt', 'Die verdächtige Anmeldung wurde blockiert.');
            }
        });
    }

    private loadAuthConfig(): void {
        if (this.orgSlug) {
            this.authApiService.getOrgAuthData(this.orgSlug)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (data) => {
                        this.localEnabled = data.localEnabled !== false;
                        this.oidcEnabled = data.oidcEnabled === true;
                        this.oidcForceLogin = data.oidcForceLogin === true;
                        this.oidcButtonText = data.oidcButtonText || 'Login with SSO';
                        this.watchSession();
                    },
                    error: () => this.fallbackOidcConfig(),
                });
        } else {
            this.authApiService.getOIDCData()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (data) => {
                        this.oidcData = data;
                        const oidcButtonText = this.oidcData.find((x: any) => x.key === 'oidc_login_button_text');
                        const oidcEnable = this.oidcData.find((x: any) => x.key === 'oidc_enabled');
                        const oidcForceLogin = this.oidcData.find((x: any) => x.key === 'oidc_force_login');
                        this.oidcEnabled = !!(oidcEnable?.valueBoolean);
                        this.oidcForceLogin = !!(oidcForceLogin?.valueBoolean);
                        if (oidcButtonText?.value) {
                            this.oidcButtonText = oidcButtonText.value;
                        }
                        this.watchSession();
                    },
                    error: () => {
                        this.showSpinner = false;
                        this.watchSession();
                    },
                });
        }
    }

    private fallbackOidcConfig(): void {
        this.authApiService.getOIDCData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => {
                    const oidcEnable = data.find((x) => x.key === 'oidc_enabled');
                    this.oidcEnabled = !!(oidcEnable?.valueBoolean);
                    this.watchSession();
                },
                error: () => this.watchSession(),
            });
    }

    private watchSession(): void {
        this.authService.sessionData
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => {
                    if (this.stage === 'reset') {
                        this.showSpinner = false;
                        return;
                    }

                    if ((!data.isLoggedIn && data.require2Fa) || data.tokenType === 'twofactortoken') {
                        this.stage = '2fa';

                        if (data.twofactors) {
                            this.twoFactorDevices = data.twofactors;

                            if (data.twofactors.length === 1) {
                                this.twofa.selectedDevice = data.twofactors[0].name!;
                            }
                        }
                    }

                    if (data.stage === 'pending_verification') {
                        this.stage = 'pending_verification';
                    }

                    if (data.isLoggedIn && data.user.expires > Date.now() && data.tokenType !== 'twofactortoken') {
                        this.redirectAfterLogin();
                    } else if (!data.isLoggedIn && !sessionStorage.getItem('logout_in_progress')) {
                        this.authService.restoreSession().then((result) => {
                            if (result) {
                                this.redirectAfterLogin();
                            } else if (this.oidcEnabled && this.oidcForceLogin && !this.router.url.includes('localLogin=true')) {
                                this.oidcLogin();
                            } else {
                                this.showSpinner = false;
                            }
                        });
                    } else if (sessionStorage.getItem('logout_in_progress')) {
                        sessionStorage.removeItem('logout_in_progress');
                        this.showSpinner = false;
                    }
                },
                error: (error) => console.error(error)
            });
    }

    private redirectAfterLogin(): void {
        if (this.loginRedirectInFlight) {
            return;
        }
        this.loginRedirectInFlight = new Promise((resolve) => {
            this.route.queryParams.pipe(take(1)).subscribe((params) => {
                const invite = typeof params['invite'] === 'string' ? params['invite'] : '';
                const returnUrl = typeof params['returnUrl'] === 'string' ? params['returnUrl'] : undefined;
                void this.finishLogin(invite, returnUrl).finally(resolve);
            });
        });
    }

    private async finishLogin(invite: string, returnUrl?: string): Promise<void> {
        if (invite) {
            this.orgService.markSkipCreateOrgPrompt();
            try {
                await firstValueFrom(this.orgApiService.acceptInvite(invite));
                this.toastService.showSuccessToast(
                    this.translateService.instant('cmp.org.inviteAccepted'),
                    this.translateService.instant('cmp.org.inviteAccepted')
                );
            } catch {
                this.toastService.showErrorToast(
                    this.translateService.instant('cmp.org.inviteAcceptFailed'),
                    this.translateService.instant('cmp.org.inviteAcceptFailed')
                );
            }
        }
        if (returnUrl) {
            void this.router.navigateByUrl(returnUrl);
            return;
        }
        void this.router.navigate(['/app']);
    }

    oidcLogin() {
        this.showSpinner = true;
        this.route.queryParams.pipe(take(1)).subscribe((params) => {
            const orgParam = this.orgSlug ? `&org=${encodeURIComponent(this.orgSlug)}` : '';
            const inviteReturn = params['invite']
                ? `/login?invite=${encodeURIComponent(params['invite'])}`
                : '';
            const returnParam = params['returnUrl']
                ? `returnUrl=${encodeURIComponent(params['returnUrl'])}`
                : inviteReturn
                  ? `returnUrl=${encodeURIComponent(inviteReturn)}`
                  : '';
            const query = [returnParam, orgParam.replace(/^&/, '')].filter(Boolean).join('&');
            window.location.href = query
                ? this.authApiService.apiUrl(`v1/auth/oidc/login?${query}`)
                : this.authApiService.apiUrl('v1/auth/oidc/login') + orgParam;
        });
    }

    async login() {
        if (this.stage === 'reset') {
            await this.submitPasswordReset();
            return;
        }

        if (this.stage === '2fa') {
            await this.submitTwoFactor();
            return;
        }

        this.validationErrors = {};

        if (this.credentials.email === '') {
            this.validationErrors['email'] = this.translateService.instant('auth.enter_email');
        }

        if (this.credentials.password === '') {
            this.validationErrors['password'] = this.translateService.instant('auth.enter_password');
        }

        if (!validateEmail(this.credentials.email)) {
            this.validationErrors['email'] = this.translateService.instant('auth.enter_valid_email');
        }

        if (Object.keys(this.validationErrors).length > 0) {
            this.validationErrors['form'] = this.translateService.instant('auth.login_data_invalid');
            this.toastService.showErrorToast(this.translateService.instant('auth.login_failed'), this.translateService.instant('auth.login_data_invalid'));
            return;
        }

        this.showSpinner = true;

        const result = await this.authService.login(this.credentials.email, this.credentials.password).catch((err) => {
            this.showSpinner = false;

            if (err && err.error && err.error.error === 'Login failed' && err.error.message === 'Provided credentials are invalid!') {
                this.validationErrors['form'] = this.translateService.instant('auth.login_data_invalid');
            } else {
                this.validationErrors['form'] = this.translateService.instant('auth.login_failed');
                this.toastService.showErrorToast(this.translateService.instant('auth.login_failed'), this.translateService.instant('auth.login_failed'));
            }
        });

        this.showSpinner = false;
        if (result && result.statusCode === 200 && result.message === 'Login successfull!') {
            this.redirectAfterLogin();
        } else if (result && result.statusCode === 200 && result.message === 'Login pending verification!') {
            this.stage = 'pending_verification';
        } else if (result && result.statusCode === 200 && result.message === 'TwoFactor is required!') {
            this.stage = '2fa';
            this.twoFactorDevices = result.data.twoFactorDevices;
            if (result.data.twoFactorDevices.length === 1) {
                this.twofa.selectedDevice = result.data.twoFactorDevices[0].name!;
            }
        }
    }

    private async submitPasswordReset(): Promise<void> {
        this.validationErrors = {};
        if (!this.newPassword || this.newPassword.length < 8) {
            this.validationErrors['password'] = this.translateService.instant('auth.password_min_length');
        }
        if (this.newPassword !== this.newPasswordRepeat) {
            this.validationErrors['passwordRepeat'] = this.translateService.instant('auth.password_mismatch');
        }
        if (Object.keys(this.validationErrors).length > 0) {
            return;
        }

        this.showSpinner = true;
        try {
            await firstValueFrom(this.authApiService.reset(this.resetToken, this.newPassword));
            this.toastService.showSuccessToast(
                this.translateService.instant('auth.password_updated'),
                this.translateService.instant('auth.password_updated_login')
            );
            this.resetToken = '';
            this.newPassword = '';
            this.newPasswordRepeat = '';
            this.stage = 'login';
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { reset: null },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        } catch {
            this.toastService.showErrorToast(
                this.translateService.instant('auth.password_update_failed'),
                this.translateService.instant('auth.password_update_failed')
            );
        } finally {
            this.showSpinner = false;
        }
    }

    async submitTwoFactor(): Promise<void> {
        if (!this.twofa.selectedDevice || !this.twofa.code) {
            this.toastService.showErrorToast('2FA', 'Bitte Gerät und Code eingeben.');
            return;
        }
        this.showSpinner = true;
        const result = await this.authService.twoFactor(this.twofa.selectedDevice, this.twofa.code).catch(() => undefined);
        this.showSpinner = false;
        if (result?.message === 'Login successfull!') {
            this.redirectAfterLogin();
        }
    }

    onPasswordKeyPress(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.login();
        }
    }
}
